import { basketSetDegraded, cartInfo } from "data/cookie";
import { getCookie, removeCookie, setCookie } from "./cookie";
import { BASKET_COOKIE_EXPIRED, colonSeparator, semicolonSeparator } from 'data/consts';
import { cartProductRegexp } from "data/regexp";

export interface BasketCookie {
  code: string;
  quantity: number;
}

export const getBasketCookie = (): BasketCookie[] | undefined => {
  const cookie = getCookie(cartInfo);

  if (!cookie) return undefined;

  const basket = mapBasketProducts(cookie, semicolonSeparator);

  if (basket.length > 0) return basket;

  removeCookie(cartInfo);

  return undefined;
};

export const mapBasketProducts = (value: string, separator: string): BasketCookie[] => {
  return value
    .split(separator)
    .filter(item => cartProductRegexp.test(item))
    .map(item => {
      const [code, quantity] = item.split(colonSeparator);
      const quantityNumber = parseFloat(quantity);
      return { code, quantity: Math.max(quantityNumber, 0) };
    })
    .filter((item, index, arr) => arr.findIndex(itemDouble => itemDouble.code === item.code) === index);
};

export const mergeProducts = (initial: BasketCookie[], merging: BasketCookie[], addQuantity = false) => {
  const clonedInitial = [...initial];

  merging.forEach(product => {
    const initialIndex = clonedInitial.findIndex(({ code }) => code === product.code);

    if (initialIndex === -1) {
      clonedInitial.push(product);
    } else {
      const { code, quantity } = clonedInitial[initialIndex];

      const newQuantity = addQuantity ? quantity + product.quantity : Math.max(quantity, product.quantity);

      clonedInitial[initialIndex] = { code, quantity: newQuantity };
    }
  });

  return clonedInitial;
};

export const setBasketCookie = (cookie: BasketCookie[]) => {
  const parseCookie = cookie
    .filter(({ quantity }) => quantity)
    .map(({ code, quantity }) => `${code}${colonSeparator}${quantity}`)
    .join(semicolonSeparator);

  if (parseCookie) {
    setCookie(cartInfo, parseCookie, {
      expires: BASKET_COOKIE_EXPIRED,
    });
  } else {
    removeCookie(cartInfo);
  }
};

export const setBasketSetDegraded = (degraded: boolean) => { // NOSONAR
  if (degraded) { // NOSONAR
    setCookie(basketSetDegraded, 'true', {
      expires: BASKET_COOKIE_EXPIRED,
    });
  } else {
    removeCookie(basketSetDegraded);
  }
};

export const updateBasketItemQuantity = (cookie: BasketCookie[], id: string, quantity: number, useMax = false): BasketCookie[] => {
  const isEmptyQuantity = quantity <= 0;

  if (isEmptyQuantity) {
    return cookie.filter(product => product.code !== id)
  }

  const hasProduct = cookie.some(product => product.code === id);

  if (hasProduct) {
    return cookie.map(product => {
      return product.code === id ? { code: id, quantity: useMax ? Math.max(product.quantity, quantity) : quantity } : product;
    });
  }

  return cookie.concat({ code: id, quantity });
};

export const deleteBasketItems = (cookie: BasketCookie[], deleteItems: (string | { code: string, quantity: number })[]): BasketCookie[] => {
  return cookie.map(basketItem => {
    const deleteItem = deleteItems.find(item => (typeof item === 'string' ? item : item.code) === basketItem.code);

    if (deleteItem) {
      return {
        ...basketItem,
        quantity: typeof deleteItem === 'string' ? 0 : basketItem.quantity - deleteItem.quantity,
      }
    }

    return basketItem;
  }).filter(({ quantity }) => quantity > 0);
};
